import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  useTheme,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getCheckInBookings, getBookingDetails } from '../services/api';
import BookingDetailsDrawer from '../components/booking/BookingDetailsDrawer';
import BookingOpsBookingCard from '../components/booking/BookingOpsBookingCard';
import CheckInDrawer from '../components/booking/CheckInDrawer';
import DashboardHeader from '../components/common/DashboardHeader';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getDashboardPageShellSx, getDashboardPaperSx, resolveDashboardThemeMode } from '../utils/dashboardSurfaceSx';
import { normalizeBookingStatusValue } from '../utils/bookingOpsCardHelpers';
import { patchBookingInList } from '../utils/bookingListPatch';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { getHotelDateFormat } from '../utils/dateUtils';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';
import {
  getBookingOpsControlsLayoutSx,
  getBookingOpsDateFieldSx,
  getBookingOpsDatePickerBoxSx,
  getBookingOpsDateRowSx,
  getBookingOpsFilterToggleSx,
  getBookingOpsFiltersRowSx,
  getBookingOpsGridItemSx,
  getBookingOpsNavButtonSx,
  getBookingOpsTotalLabelSx,
  getBookingOpsTotalRowSx,
  getBookingOpsTotalValueSx,
} from '../utils/bookingOperationsMobileLayout';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const CheckInPage = () => {
  const normalizeBookingId = (value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };
  const theme = useTheme();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isMobileLayout = isMobileShellLayout();
  const isDark = resolveDashboardThemeMode(currentTheme) === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkInDrawerOpen, setCheckInDrawerOpen] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'checkin', 'pending', 'cancelled'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const fetchAbortController = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel any ongoing fetch
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Debounce the fetch
    debounceTimer.current = setTimeout(() => {
      setHasFetched(false); // Reset fetch status when date changes
      fetchBookings();
      setCurrentPage(1); // Reset to first page when date changes
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [selectedDate]);

  const fetchBookings = useCallback(async () => {
    // Create new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    const signal = fetchAbortController.current.signal;

    setLoading(true);
    setError(null);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const data = await getCheckInBookings(dateStr);
      if (signal.aborted) return;
      setBookings(data.bookings || []);
      setHasFetched(true);
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {setError('Failed to load check-in bookings. Please try again.');
        setHasFetched(true); // Set to true even on error so we can show error message
      }
    } finally {
      if (!fetchAbortController.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedDate]);

  /** Update one card after drawer payment — no full-list loading flash. */
  const refreshBookingCard = useCallback(async (bookingIdArg) => {
    const id = normalizeBookingId(bookingIdArg ?? selectedBookingId);
    if (!id) return;
    try {
      const details = await getBookingDetails(id);
      setBookings((prev) => patchBookingInList(prev, id, details, normalizeBookingId));
    } catch {
      // Keep existing cards; avoid forcing a full-list reload.
    }
  }, [selectedBookingId]);

  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(moment());
  };

  const handleBookingClick = (bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setSelectedBookingId(safeBookingId);
    // Open check-in drawer instead of booking details drawer
    setCheckInDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleCheckInDrawerClose = () => {
    setCheckInDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleCheckInSuccess = (booking) => {
    // Refresh bookings list after successful check-in
    fetchBookings();
    setCheckInDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleModifyBooking = (bookingId) => {
    if (!canEditBooking) return;
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    // Close check-in drawer and navigate to edit booking page
    setCheckInDrawerOpen(false);
    navigate(`/booking/edit/${safeBookingId}/`);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
    }
  };

  // Filter bookings based on selected filter - memoized for performance
  const filteredBookings = useMemo(() => {
    const getStatus = (booking) => normalizeBookingStatusValue(booking);

    if (filterType === 'all') {
      return bookings;
    }
    if (filterType === 'checkin') {
      return bookings.filter((booking) => getStatus(booking) === 'checked_in');
    }
    if (filterType === 'pending') {
      return bookings.filter((booking) => {
        const status = getStatus(booking);
        return status === 'confirmed'
          || status === 'reserved'
          || status === 'pending'
          || status === 'partial_checkin'
          || status === 'partial_checkout';
      });
    }
    if (filterType === 'cancelled') {
      return bookings.filter((booking) => {
        const status = getStatus(booking);
        return status === 'cancelled' || status === 'canceled';
      });
    }
    return bookings;
  }, [bookings, filterType]);

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBookings.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Check-In"
            forceDark={isMobileShellLayout()}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Booking', path: '/booking/' },
              { label: 'Check-In' }
            ]}
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              { url: '/booking/reservation/', label: 'New Reservation' },
            ]}
          />
        </Box>

        {/* Date Navigation and Total Checkin - Same style as dashboard */}
        <Paper
          elevation={0}
          sx={getDashboardPaperSx(currentTheme, 'controls')}
        >
          <Box sx={getBookingOpsControlsLayoutSx(isMobileLayout)}>
            <Box sx={getBookingOpsDateRowSx(isMobileLayout)} className="dashboard-date-range">
              <IconButton
                onClick={handlePrevClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={getBookingOpsDatePickerBoxSx(isMobileLayout)} className="calendar-date-picker">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: getBookingOpsDateFieldSx(isDark, CUSTOM_BORDER_COLOR),
                    },
                  }}
                />
              </Box>
              <IconButton
                onClick={handleNextClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            <Box sx={getBookingOpsFiltersRowSx(isMobileLayout)}>
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="filter type"
                size="small"
                sx={getBookingOpsFilterToggleSx(isMobileLayout, isDark, CUSTOM_BORDER_COLOR)}
              >
                <ToggleButton value="all" aria-label="all">
                  All
                </ToggleButton>
                <ToggleButton value="checkin" aria-label="checkin">
                  Checkin
                </ToggleButton>
                <ToggleButton value="pending" aria-label="pending">
                  Pending
                </ToggleButton>
                <ToggleButton value="cancelled" aria-label="cancelled">
                  Cancelled
                </ToggleButton>
              </ToggleButtonGroup>

              <Box sx={getBookingOpsTotalRowSx(isMobileLayout, isDark)}>
                <Typography variant="body2" sx={getBookingOpsTotalLabelSx(isDark)}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={getBookingOpsTotalValueSx(isDark)}>
                  {filteredBookings.length}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Bookings Grid */}
      {!loading && !error && hasFetched && (
        <>
          {filteredBookings.length === 0 ? (
            <EmptyStateCard themeMode={currentTheme}>
              No bookings found for {selectedDate.format('DD MMM YYYY')}
            </EmptyStateCard>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedBookings.map((booking) => {
                  const resolvedBookingId = normalizeBookingId(booking?.id || booking?.booking_id);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={getBookingOpsGridItemSx()}
                      key={resolvedBookingId || `${booking?.code || 'booking'}_${booking?.check_in_date || ''}_${booking?.check_out_date || ''}`}
                    >
                      <BookingOpsBookingCard
                        booking={booking}
                        isDark={isDark}
                        accent={CUSTOM_BORDER_COLOR}
                        isMobile={isMobileLayout}
                        onClick={() => handleBookingClick(resolvedBookingId)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: isDark ? '#ddd' : '#333',
                        '&.Mui-selected': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                          color: '#000',
                          '&:hover': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Check-In Drawer */}
      {selectedBookingId && (
        <CheckInDrawer
          bookingId={selectedBookingId}
          open={checkInDrawerOpen}
          onClose={handleCheckInDrawerClose}
          onCheckInSuccess={handleCheckInSuccess}
          onModifyBooking={canEditBooking ? handleModifyBooking : undefined}
          onBookingUpdated={refreshBookingCard}
        />
      )}

      {/* Booking Details Drawer (for reference, can be opened via other means) */}
      {selectedBookingId && (
        <BookingDetailsDrawer
          bookingId={selectedBookingId}
          open={drawerOpen}
          onClose={handleDrawerClose}
          onStatusUpdate={refreshBookingCard}
        />
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default CheckInPage;

